import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import {
  donationInTaxYearPeriod,
  isDonorAddedDonation,
  sumDonationAmounts,
  sumGiftAidAmounts,
} from '@/helpers/taxPackDonations'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'

interface StatementBaseTotals {
  totalDonations: number
  totalGiftAid: number
}

/**
 * Combines the Swiftaid statement totals for a tax-year period with the donor's
 * manual (donor-added) donations for that same period. Shared by the totals
 * cards (expanded panel) and the historic row header so the two can't drift.
 */
export function useCombinedStatementTotals(
  period: MaybeRefOrGetter<string>,
  baseTotals: MaybeRefOrGetter<StatementBaseTotals>
) {
  const { manualDonations } = useManualGivingDonations()

  // Filter by period only (no sorting needed — we just sum the results).
  const manualForPeriod = computed(() =>
    manualDonations.value.filter(
      (d) => isDonorAddedDonation(d) && donationInTaxYearPeriod(d, toValue(period))
    )
  )

  const showAddedSummary = computed(() => manualForPeriod.value.length > 0)

  const addedTotalPence = computed(() => sumDonationAmounts(manualForPeriod.value))
  const addedGiftAidPence = computed(() => sumGiftAidAmounts(manualForPeriod.value))

  const combinedDonatedPence = computed(
    () => toValue(baseTotals).totalDonations + addedTotalPence.value
  )
  const combinedGiftAidPence = computed(
    () => toValue(baseTotals).totalGiftAid + addedGiftAidPence.value
  )

  return {
    manualForPeriod,
    showAddedSummary,
    addedTotalPence,
    addedGiftAidPence,
    combinedDonatedPence,
    combinedGiftAidPence,
  }
}
